"use client";

import { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useHotkeys } from "react-hotkeys-hook";
import { useQueryClient } from "@tanstack/react-query";
import { usePipelineViewStore } from "@/features/jobs/store/pipeline/view";
import { buildPipelineActionBody } from "@/features/jobs/store/pipeline/selection";
import { Icon } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { KbdShortcut } from "@/components/ui/kbd-shortcut";
import { MoveToStage } from "./move-to-stage";
import {
  ExportModal,
  type ExportOptions,
} from "@/features/search/components/actions/export";
import {
  profileExportAdapter,
  profileExportLabel,
  useExportBusy,
  useStartExportJob,
} from "@/features/exports/tracker";
import {
  requestExport,
  type ExportFormat,
  type ExportSource,
} from "@/hooks/use-search";
import { bulkAdvance } from "@/lib/api/generated";
import type { PipelineCandidate } from "@/lib/api/types";
import { usePipelineJob } from "@/features/jobs/context/pipeline-job-context";
import { Button, SubmitButton } from "@/components/ui/button";
import { JobEnrollAction } from "@/features/enrollment/components/job-enroll-action";
import { useStartSimilarSearch } from "@/hooks/use-start-similar-search";
import { isTextEditingTarget } from "@/lib/dom/is-text-editing-target";
import { getArchiveStage } from "./archive-stage";
import { invalidateSyncPreflight } from "@/features/ats-sync/lib/invalidate-after-sync";
import { useJobAtsLink } from "@/features/ats-sync/hooks/use-job-ats-link";
import { useSyncFlow } from "@/features/ats-sync/components/sync/use-sync-flow";
import { bulkSelectionSoftWarnLabel } from "./bulk-selection-limit";
import { pipelineExportScope } from "./export-jobs";

interface PipelineBulkActionsProps {
  candidates: PipelineCandidate[];
  onMoveToStage: (stageId: string) => Promise<void> | void;
  onArchive: () => Promise<void> | void;
  onDeselect: () => void;
}

export function PipelineBulkActions({
  candidates,
  onMoveToStage,
  onArchive,
  onDeselect,
}: PipelineBulkActionsProps) {
  const { job, stages, onRefresh } = usePipelineJob();
  const {
    bulkMode,
    selectedCandidateIds,
    excludedCandidateIds,
    selectionTotal,
    selectedStageId,
    filterSearch,
    filters,
  } = usePipelineViewStore();
  const { isLinked } = useJobAtsLink(job.id);
  const { open: openSyncFlow } = useSyncFlow();
  const queryClient = useQueryClient();
  const isAllMode = bulkMode === "all";
  const count = isAllMode
    ? Math.max(selectionTotal - excludedCandidateIds.size, 0)
    : selectedCandidateIds.size;
  const softWarn = bulkSelectionSoftWarnLabel(count);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const exportScope = pipelineExportScope.bulk(job.id);
  const exporting = useExportBusy(exportScope);
  const startExportJob = useStartExportJob();

  const sortedStages = useMemo(
    () => [...stages].sort((a, b) => a.position - b.position),
    [stages],
  );

  const archivedStage = useMemo(() => getArchiveStage(stages), [stages]);

  // In `all` mode only the loaded page's candidates are in hand; the rest are
  // resolved server-side. Per-candidate gating (next-stage / archived checks)
  // therefore only applies to explicit `include` selections — for `all` mode
  // the backend advances eligible candidates and skips the rest.
  const selectedCandidates = useMemo(
    () =>
      candidates.filter((c) =>
        isAllMode
          ? !excludedCandidateIds.has(c.job_candidate_id)
          : selectedCandidateIds.has(c.job_candidate_id),
      ),
    [candidates, isAllMode, excludedCandidateIds, selectedCandidateIds],
  );

  const hasArchivedSelection = useMemo(() => {
    if (isAllMode || !archivedStage) return false;
    return selectedCandidates.some((c) => c.stage_id === archivedStage.id);
  }, [isAllMode, selectedCandidates, archivedStage]);

  const canAdvance = useMemo(() => {
    if (count === 0) return false;
    if (isAllMode) return true;
    return selectedCandidates.every((c) => {
      const idx = sortedStages.findIndex((s) => s.id === c.stage_id);
      if (idx < 0 || idx >= sortedStages.length - 1) return false;
      const next = sortedStages[idx + 1];
      return !archivedStage || next.id !== archivedStage.id;
    });
  }, [count, isAllMode, selectedCandidates, sortedStages, archivedStage]);

  const canReject = useMemo(
    () => count > 0 && !!archivedStage && (isAllMode || !hasArchivedSelection),
    [count, isAllMode, hasArchivedSelection, archivedStage],
  );

  const advanceDisabledReason = useMemo(() => {
    if (count === 0 || isAllMode) return null;
    if (hasArchivedSelection) return "Some selected profiles are archived";
    if (!canAdvance) return "Some selected profiles have no next stage";
    return null;
  }, [count, isAllMode, hasArchivedSelection, canAdvance]);

  const rejectDisabledReason = useMemo(() => {
    if (count === 0 || isAllMode) return null;
    if (hasArchivedSelection)
      return "Some selected profiles are already archived";
    return null;
  }, [count, isAllMode, hasArchivedSelection]);

  const [advancing, setAdvancing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleAdvance = useCallback(async () => {
    if (!canAdvance || advancing) return;
    setAdvancing(true);
    try {
      const body = buildPipelineActionBody();
      if (!body) return;
      // The Advance endpoint moves each candidate to its own next stage and
      // skips any with no applicable next stage — no per-stage target math here.
      const { updated } = await bulkAdvance(job.id, body);
      showToast.success(
        `Advanced ${updated} ${updated === 1 ? "profile" : "profiles"}`,
      );
      onDeselect();
      onRefresh();
      invalidateSyncPreflight(queryClient, job.id);
    } catch (err) {
      showToast.error("Failed to advance profiles", {
        description: getApiErrorMessage(err),
      });
      onRefresh();
    } finally {
      setAdvancing(false);
    }
  }, [canAdvance, advancing, job.id, onDeselect, onRefresh, queryClient]);

  const handleReject = useCallback(async () => {
    if (!canReject || rejecting) return;
    setRejecting(true);
    try {
      // Success feedback is owned by the completion points: the ATS reject
      // modal (RejectInAtsModal) toasts on submit, and the local archive
      // mutation toasts on success. onArchive may only OPEN that modal and
      // resolve immediately, so a success toast here would fire before the
      // candidate is actually rejected.
      await onArchive();
    } catch (err) {
      showToast.error("Failed to reject profiles", {
        description: getApiErrorMessage(err),
      });
    } finally {
      setRejecting(false);
    }
  }, [canReject, rejecting, onArchive]);

  const handleConfirmMove = async (stageId: string) => {
    setMoving(true);
    try {
      await onMoveToStage(stageId);
      setMoveModalOpen(false);
      // Parent bulkMoveMutation owns all success messaging (immediate toast
      // or async progress poll). Do not toast success here — that doubles.
    } catch (err) {
      showToast.error("Failed to move profiles", {
        description: getApiErrorMessage(err),
      });
    } finally {
      setMoving(false);
    }
  };

  const exportSource = useMemo<ExportSource | null>(() => {
    if (count === 0) return null;
    if (isAllMode) {
      return {
        type: "pipeline",
        job_id: job.id,
        bulk: { mode: "all", exclude_ids: Array.from(excludedCandidateIds) },
        stage_id: selectedStageId ?? undefined,
        search: filterSearch || undefined,
        filters: filters ?? undefined,
      };
    }
    return {
      type: "pipeline",
      job_id: job.id,
      job_candidate_ids: Array.from(selectedCandidateIds),
    };
  }, [
    count,
    isAllMode,
    excludedCandidateIds,
    selectedCandidateIds,
    job.id,
    selectedStageId,
    filterSearch,
    filters,
  ]);

  const handleExport = useCallback(
    async (
      format: ExportFormat,
      includeContacts: boolean,
      options: ExportOptions,
    ) => {
      if (!exportSource) return;
      const started = await startExportJob({
        scope: exportScope,
        label: profileExportLabel(count, format),
        format,
        adapter: profileExportAdapter,
        reducible: true,
        post: () =>
          requestExport({
            source: exportSource,
            format,
            include_contacts: includeContacts,
            options,
          }),
      });
      if (started.ok) onDeselect();
    },
    [count, exportScope, exportSource, onDeselect, startExportJob],
  );

  const isBusy = advancing || rejecting || moving || syncing;

  const { start: startSimilar, isPending: similarPending } =
    useStartSimilarSearch(job.project_id ?? null);

  const selectedProfileIds = useMemo(
    () =>
      selectedCandidates
        .map((c) => c.profile_id)
        .filter((id): id is string => Boolean(id)),
    [selectedCandidates],
  );

  const canFindSimilar =
    !isAllMode &&
    selectedProfileIds.length >= 1 &&
    selectedProfileIds.length <= 10 &&
    !!job.project_id;

  const findSimilarDisabledReason = isAllMode
    ? "Select individual profiles to find similar"
    : !job.project_id
      ? "Job has no project"
      : selectedProfileIds.length > 10
        ? "Select up to 10 profiles"
        : null;

  const handleSync = useCallback(async () => {
    if (count === 0 || syncing) return;
    setSyncing(true);
    try {
      const body = buildPipelineActionBody();
      if (!body) return;
      const onDone = () => {
        onDeselect();
        onRefresh();
      };

      if (body.bulk) {
        openSyncFlow({ jobId: job.id, selection: body, onDone });
        return;
      }

      const names: Record<string, string> = {};
      for (const candidate of selectedCandidates) {
        if (candidate.full_name) {
          names[candidate.job_candidate_id] = candidate.full_name;
        }
      }
      openSyncFlow({
        jobId: job.id,
        jobCandidateIds: body.job_candidate_ids ?? [],
        names: Object.keys(names).length > 0 ? names : undefined,
        onDone,
      });
    } finally {
      setSyncing(false);
    }
  }, [
    count,
    syncing,
    job.id,
    selectedCandidates,
    openSyncFlow,
    onDeselect,
    onRefresh,
  ]);

  const handleFindSimilar = useCallback(async () => {
    if (!canFindSimilar) return;
    await startSimilar(selectedProfileIds);
    // Clear the selection so the bar doesn't linger on the new search page.
    onDeselect();
  }, [canFindSimilar, selectedProfileIds, startSimilar, onDeselect]);

  useHotkeys(
    "a",
    (e) => {
      if (isTextEditingTarget(e.target)) return;
      e.preventDefault();
      handleAdvance();
    },
    {
      enabled: count > 0 && canAdvance && !isBusy,
      enableOnFormTags: true,
      enableOnContentEditable: false,
    },
    [handleAdvance],
  );

  useHotkeys(
    "r",
    (e) => {
      if (isTextEditingTarget(e.target)) return;
      e.preventDefault();
      handleReject();
    },
    {
      enabled: count > 0 && canReject && !isBusy,
      enableOnFormTags: true,
      enableOnContentEditable: false,
    },
    [handleReject],
  );

  useHotkeys(
    "s",
    (e) => {
      if (isTextEditingTarget(e.target)) return;
      e.preventDefault();
      handleSync();
    },
    {
      enabled: count > 0 && isLinked && !isBusy,
      enableOnFormTags: true,
      enableOnContentEditable: false,
    },
    [handleSync],
  );

  function wrapTooltip(btn: React.ReactNode, label: string) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>{btn}</span>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <>
      <AnimatePresence>
        {count > 0 && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 400,
              mass: 0.5,
            }}
            className="absolute bottom-10 left-0 right-0 z-50 flex flex-col gap-2 border-t border-border bg-surface-elevated px-3 py-2.5 shadow-[0px_-4px_12px_0px_rgba(0,0,0,0.08)] md:bottom-[92px] md:left-1/2 md:right-auto md:w-auto md:max-w-[calc(100%-2rem)] md:-translate-x-1/2 md:flex-row md:flex-nowrap md:items-center md:gap-4 md:rounded-xl md:border md:px-3 md:py-3 md:shadow-[var(--modal-shadow)]"
          >
            <div className="flex items-center justify-between md:contents">
              <div className="flex flex-col gap-0.5 md:flex-row md:items-center md:gap-2">
                <span className="whitespace-nowrap text-sm font-medium text-muted-foreground">
                  {count} Selected
                </span>
                {softWarn ? (
                  <span
                    className="whitespace-nowrap text-xs text-amber-600 dark:text-amber-400"
                    aria-live="polite"
                  >
                    {softWarn}
                  </span>
                ) : null}
              </div>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={onDeselect}
                aria-label="Clear selection"
                className="size-7 text-muted-foreground hover:opacity-100 hover:bg-contrast/6 hover:text-foreground md:hidden"
              >
                <Icon name="IconCrossMedium" size={14} />
              </Button>
            </div>

            <div className="grid grid-cols-4 gap-1.5 md:contents">
              <div className="hidden md:block h-5 w-px shrink-0 bg-border" />

              {wrapTooltip(
                <Button
                  size="sm"
                  variant="success-subtle"
                  onClick={handleAdvance}
                  disabled={!canAdvance || isBusy}
                  className="w-full md:w-auto"
                  icon={
                    <Icon
                      name={advancing ? "IconLoadingCircle" : "IconCircleCheck"}
                      className={advancing ? "animate-spin" : undefined}
                    />
                  }
                >
                  <span className="hidden min-[1600px]:inline">Advance</span>
                  <span className="hidden min-[1600px]:inline-flex">
                    <KbdShortcut
                      keys={["A"]}
                      variant="primary"
                      size="icon-xs"
                    />
                  </span>
                </Button>,
                advanceDisabledReason ?? "Advance",
              )}

              {wrapTooltip(
                <Button
                  size="sm"
                  variant="destructive-subtle"
                  onClick={handleReject}
                  disabled={!canReject || isBusy}
                  className="w-full md:w-auto"
                  icon={
                    <Icon
                      name={rejecting ? "IconLoadingCircle" : "IconCircleX"}
                      className={rejecting ? "animate-spin" : undefined}
                    />
                  }
                >
                  <span className="hidden min-[1600px]:inline">Reject</span>
                  <span className="hidden min-[1600px]:inline-flex">
                    <KbdShortcut
                      keys={["R"]}
                      variant="primary"
                      size="icon-xs"
                    />
                  </span>
                </Button>,
                rejectDisabledReason ?? "Reject",
              )}

              {isLinked && (
                <Button
                  size="sm"
                  variant="info-subtle"
                  onClick={handleSync}
                  disabled={isBusy}
                  className="w-full md:w-auto"
                  icon={
                    <Icon
                      name={
                        syncing
                          ? "IconLoadingCircle"
                          : "IconArrowsRepeatRightLeft"
                      }
                      className={syncing ? "animate-spin" : undefined}
                    />
                  }
                >
                  <span>Sync</span>
                  <span className="hidden min-[1600px]:inline-flex">
                    <KbdShortcut
                      keys={["S"]}
                      variant="primary"
                      size="icon-xs"
                    />
                  </span>
                </Button>
              )}

              <JobEnrollAction
                jobId={job.id}
                jobCandidateIds={selectedCandidates.map(
                  (c) => c.job_candidate_id,
                )}
                disabled={isBusy || isAllMode}
                className="hidden md:inline-flex"
              />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setMoveModalOpen(true)}
                    disabled={isBusy}
                    className="w-full md:w-auto"
                    icon={<Icon name="IconPlan" />}
                  >
                    <span className="hidden min-[1600px]:inline">
                      Move to Stage
                    </span>
                    <span className="min-[1600px]:hidden md:hidden">Move</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="hidden md:block min-[1600px]:hidden"
                >
                  Move to Stage
                </TooltipContent>
              </Tooltip>

              {wrapTooltip(
                <SubmitButton
                  size="sm"
                  variant="secondary"
                  icon={<Icon name="IconPersona" />}
                  onClick={handleFindSimilar}
                  disabled={!canFindSimilar || isBusy}
                  loading={similarPending}
                  loadingLabel="Starting"
                  className="hidden md:inline-flex"
                >
                  <span className="hidden min-[1600px]:inline">
                    Find Similar Profiles
                  </span>
                </SubmitButton>,
                findSimilarDisabledReason ?? "Find Similar Profiles",
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setExportModalOpen(true)}
                    disabled={isBusy || exporting}
                    className="w-full md:w-auto"
                    icon={
                      <Icon
                        name={exporting ? "IconLoadingCircle" : "IconArrowInbox"}
                        className={exporting ? "animate-spin" : undefined}
                      />
                    }
                  >
                    <span className="hidden min-[1600px]:inline">Export</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="min-[1600px]:hidden">
                  Export
                </TooltipContent>
              </Tooltip>

              <div className="hidden md:block h-5 w-px shrink-0 bg-border" />
            </div>

            <Button
              size="icon-sm"
              variant="ghost"
              onClick={onDeselect}
              aria-label="Clear selection"
              className="hidden md:flex size-7 text-muted-foreground hover:opacity-100 hover:bg-contrast/6 hover:text-foreground"
            >
              <Icon name="IconCrossMedium" size={14} />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <MoveToStage
        open={moveModalOpen}
        onOpenChange={setMoveModalOpen}
        jobId={job.id}
        onConfirm={handleConfirmMove}
        loading={moving}
        count={count}
      />

      <ExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        profileCount={count}
        source={exportSource}
        onExport={handleExport}
      />
    </>
  );
}
